import { can, formatDate } from '@clim/client-core';
import { formatEuros, type PaymentRecord } from '@clim/domain';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PaymentBadge, PaymentDialog } from '../components/DossierPayments';
import { Empty, ErrorBox } from '../components/ui';
import { errorMessage } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';

/**
 * Encaissements à valider (§10) : espèces et chèques « à remettre », virements à constater,
 * regroupés par collecteur ; confirmation par une personne autorisée (jamais le collecteur).
 */
export function RemittancesPage() {
  const engine = useEngine();
  const { me } = useReady();
  const { territoryId } = useTerritory();
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = engine
    .list('payment')
    .filter((p) => p.methodKind !== 'stripe' && ['to_remit', 'pending'].includes(p.status) && (!territoryId || p.territoryId === territoryId))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const byCollector = new Map<string, PaymentRecord[]>();
  for (const p of pending) byCollector.set(p.collectedByName, [...(byCollector.get(p.collectedByName) ?? []), p]);
  const canConfirm = can(me, 'payment.cash.confirm');
  const confirmAll = async (list: PaymentRecord[]) => {
    setError(null);
    try {
      for (const p of list) if (p.collectedById !== me.user.id) await engine.transitionPayment(p.id, 'succeeded');
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <div>
      <h1>Encaissements à valider</h1>
      <p className="muted">Espèces et chèques remis par les équipes, virements à constater. Chaque confirmation est tracée (qui, quand).</p>
      <ErrorBox message={error} />
      {pending.length === 0 && <Empty>Rien à valider.</Empty>}
      {[...byCollector.entries()].map(([name, list]) => {
        const total = list.reduce((s, p) => s + p.amountCents, 0);
        const cash = list.filter((p) => p.methodKind === 'cash').reduce((s, p) => s + p.amountCents, 0);
        return (
          <div className="card" key={name}>
            <div className="card-head">
              <h2>
                {name} <span className="muted">· {list.length} encaissement(s) · {formatEuros(total)}</span>
              </h2>
              {canConfirm && list.some((p) => p.collectedById !== me.user.id) && (
                <button className="btn small primary" onClick={() => void confirmAll(list)}>
                  Tout confirmer
                </button>
              )}
            </div>
            {cash > 0 && <p className="muted">Dont espèces : {formatEuros(cash)}</p>}
            <ul className="plain payments">
              {list.map((p) => {
                const d = engine.get('dossier', p.dossierId);
                return (
                  <li key={p.id}>
                    <button type="button" className="row" onClick={() => setOpen(p.id)}>
                      <span className="row-main">
                        <strong>
                          {p.methodLabel} · {formatEuros(p.amountCents)}
                        </strong>
                        <span className="muted">
                          {d?.reference ?? '—'} · {p.receivedOn ? p.receivedOn.split('-').reverse().join('/') : formatDate(p.createdAt)}
                          {p.reference ? ` · réf. ${p.reference}` : ''}
                        </span>
                      </span>
                      <PaymentBadge p={p} />
                    </button>
                    {d && (
                      <Link className="muted small" to={`/dossiers/${d.id}`}>
                        Dossier →
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      {open && <PaymentDialog id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
